import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Grid,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SendIcon from "@mui/icons-material/Send";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import BottomNav from "./BottomNav";
import { PageShell, PageHeader, PageContent, Section } from "./Page";

export const SUPPORT_EMAIL = "support@dapurtecnologia.com";
// FormSubmit reenvía el formulario por email sin necesidad de backend
const FORM_ENDPOINT = `https://formsubmit.co/ajax/${SUPPORT_EMAIL}`;

const faqs = [
  {
    category: "Publicaciones",
    question: "¿Cómo publico un vehículo?",
    answer:
      "Entrá en «Publicar», completá los datos del vehículo, la ubicación y la documentación, subí las fotos y tocá «Publicar». La publicación aparece en Inicio al instante.",
  },
  {
    category: "Publicaciones",
    question: "¿Puedo editar o eliminar una publicación?",
    answer:
      "Sí. En «Mis vehículos» cada publicación tiene los botones «Editar» y «Eliminar». Desde «Editar» podés cambiar el precio, la descripción, la ubicación y las fotos.",
  },
  {
    category: "Publicaciones",
    question: "¿Para qué piden DNI/CUIT y patente?",
    answer:
      "Los usamos para validar que quien publica es el titular o está autorizado a vender. Estos datos no se muestran en la publicación.",
  },
  {
    category: "Publicaciones",
    question: "¿Cómo marco un vehículo como vendido?",
    answer:
      "Abrí el chat con el comprador y tocá «Marcar como vendido» en la parte superior. La publicación queda marcada como vendida.",
  },
  {
    category: "Chats",
    question: "¿Cómo contacto a un vendedor?",
    answer:
      "Abrí la publicación y tocá «Consultar al vendedor». Se abre un chat privado; las conversaciones quedan guardadas en la sección «Chats».",
  },
  {
    category: "Seguridad",
    question: "¿Qué recaudos tengo que tomar al comprar?",
    answer:
      "Nunca hagas pagos ni señas por adelantado sin ver el vehículo. Revisá la documentación, verificá la patente, encontrate en lugares públicos y, si podés, hacé revisar el auto por un mecánico de confianza.",
  },
  {
    category: "Seguridad",
    question: "¿Cómo denuncio una publicación sospechosa?",
    answer:
      "Escribinos con el formulario de abajo eligiendo el motivo «Denunciar una publicación» e incluí el título del vehículo. Lo revisamos a la brevedad.",
  },
  {
    category: "Cuenta",
    question: "Olvidé mi contraseña, ¿qué hago?",
    answer:
      "En la pantalla de inicio de sesión escribí tu email y tocá «¿Olvidaste tu contraseña?». Te enviamos un enlace para crear una nueva.",
  },
  {
    category: "Cuenta",
    question: "¿Cómo cambio mis datos o mi foto de perfil?",
    answer:
      "En «Perfil» tocá el ícono de la cámara sobre tu foto para cambiarla, o entrá en «Editar perfil» para actualizar tu nombre, teléfono y dirección.",
  },
];

const reasons = [
  "Consulta general",
  "Problema con mi cuenta",
  "Problema con una publicación",
  "Denunciar una publicación",
  "Sugerencia",
  "Otro",
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// honey: campo trampa invisible; si viene completo es un bot y FormSubmit descarta el envío
const emptyForm = { name: "", email: "", reason: reasons[0], subject: "", message: "", honey: "" };

const Support = () => {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  // Precargar nombre y email del usuario logueado
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) return;
      let name = currentUser.displayName || "";
      try {
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (userDoc.exists()) name = userDoc.data().displayName || name;
      } catch {
        // Error ignorado intencionalmente
      }
      setForm((prev) => ({
        ...prev,
        name: prev.name || name,
        email: prev.email || currentUser.email || "",
      }));
    });
    return () => unsubscribe();
  }, []);

  const filteredFaqs = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return faqs;
    return faqs.filter((faq) => `${faq.question} ${faq.answer}`.toLowerCase().includes(query));
  }, [search]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Ingresá tu nombre.";
    if (!EMAIL_PATTERN.test(form.email.trim())) next.email = "Ingresá un email válido.";
    if (!form.subject.trim()) next.subject = "Ingresá un asunto.";
    if (form.message.trim().length < 10) next.message = "Contanos un poco más (mínimo 10 caracteres).";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSending(true);
    setStatus(null);

    const name = form.name.trim();
    const email = form.email.trim();
    const subject = `[Caro · ${form.reason}] ${form.subject.trim()}`;
    const message = form.message.trim();

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          _subject: subject,
          _replyto: email,
          _template: "table",
          _captcha: "false",
          _honey: form.honey,
          Nombre: name,
          Email: email,
          Motivo: form.reason,
          Mensaje: message,
        }),
      });
      const result = await response.json().catch(() => ({}));
      // El primer envío desde un sitio nuevo queda pendiente hasta confirmar el email de activación de FormSubmit
      if (/activat/i.test(result.message || "")) {
        setStatus({ severity: "info", message: "El formulario se está activando. Probá de nuevo en unos minutos." });
        return;
      }
      if (!response.ok || String(result.success) !== "true") {
        throw new Error(result.message || `FormSubmit respondió ${response.status}`);
      }
      setStatus({ severity: "success", message: "¡Gracias! Recibimos tu mensaje y te vamos a responder por email." });
      setForm((prev) => ({ ...emptyForm, name: prev.name, email: prev.email }));
    } catch (error) {
      setStatus({
        severity: "error",
        message: `No pudimos enviar tu mensaje. Probá de nuevo o escribinos a ${SUPPORT_EMAIL}.`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <PageShell>
      <PageHeader title="Soporte" subtitle="Preguntas frecuentes y contacto" />
      <PageContent>
        <Stack spacing={3}>
          {/* Preguntas frecuentes */}
          <Section title="Preguntas frecuentes" description="Las respuestas a las consultas más comunes.">
            <TextField
              placeholder="Buscar en las preguntas..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            {filteredFaqs.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                No encontramos preguntas con ese texto. Escribinos con el formulario de abajo.
              </Typography>
            ) : (
              <Box
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                {filteredFaqs.map((faq, index) => (
                  <Accordion
                    key={faq.question}
                    disableGutters
                    elevation={0}
                    square
                    expanded={expanded === faq.question}
                    onChange={(_, isOpen) => setExpanded(isOpen ? faq.question : false)}
                    sx={{
                      borderTop: index > 0 ? 1 : 0,
                      borderColor: "divider",
                      "&::before": { display: "none" },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5, py: 0.5 }}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: "secondary.dark", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em" }}
                        >
                          {faq.category}
                        </Typography>
                        <Typography sx={{ fontWeight: 700 }}>{faq.question}</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </Section>

          {/* Formulario de contacto */}
          <Section
            title="¿No encontraste lo que buscabas?"
            description="Escribinos y te respondemos por email lo antes posible."
          >
            <Box component="form" noValidate onSubmit={handleSubmit}>
              <Box
                component="input"
                type="text"
                name="honey"
                value={form.honey}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                sx={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nombre"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField select label="Motivo" name="reason" value={form.reason} onChange={handleChange} fullWidth>
                    {reasons.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {reason}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField
                    label="Asunto"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    error={!!errors.subject}
                    helperText={errors.subject}
                    inputProps={{ maxLength: 120 }}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Mensaje"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    error={!!errors.message}
                    helperText={errors.message || `${form.message.length}/2000`}
                    inputProps={{ maxLength: 2000 }}
                    required
                    multiline
                    rows={6}
                    fullWidth
                  />
                </Grid>
              </Grid>

              {status && (
                <Alert severity={status.severity} sx={{ mt: 3 }}>
                  {status.message}
                </Alert>
              )}

              <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={2}
                sx={{ mt: 3 }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "text.secondary" }}>
                  <MailOutlineIcon fontSize="small" />
                  <Typography variant="body2">
                    También podés escribir a{" "}
                    <Link href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: "secondary.dark", fontWeight: 600 }}>
                      {SUPPORT_EMAIL}
                    </Link>
                  </Typography>
                </Stack>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  size="large"
                  endIcon={<SendIcon />}
                  disabled={sending}
                >
                  {sending ? "Enviando..." : "Enviar mensaje"}
                </Button>
              </Stack>
            </Box>
          </Section>
        </Stack>
      </PageContent>
      <BottomNav />
    </PageShell>
  );
};

export default Support;
